import { useCallback, useEffect, useRef, useState } from 'react';
import { Excalidraw, exportToBlob } from '@excalidraw/excalidraw';
import jsPDF from 'jspdf';

// Short delay so Excalidraw's ResizeObserver fires after the layout has settled.
const MOUNT_DELAY_MS = 80;

// These tools reset to selection after each use by design; don't lock them.
const NO_LOCK_TOOLS = new Set(['selection', 'hand', 'eraser']);

async function downloadAsPDF(api) {
  const elements = api.getSceneElements();
  if (!elements.length) return;

  const blob = await exportToBlob({
    elements,
    appState: { ...api.getAppState(), exportWithDarkMode: false },
    files: api.getFiles(),
    mimeType: 'image/png',
    quality: 1,
  });

  const imgUrl = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => {
    const pdf = new jsPDF({
      orientation: img.width > img.height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [img.width, img.height],
    });
    pdf.addImage(img, 'PNG', 0, 0, img.width, img.height);
    pdf.save('whiteboard.pdf');
    URL.revokeObjectURL(imgUrl);
  };
  img.src = imgUrl;
}

export default function WhiteboardPanel() {
  const [ready, setReady] = useState(false);
  const apiRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), MOUNT_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const setAPI = useCallback((api) => {
    if (!api) return;
    apiRef.current = api;
    requestAnimationFrame(() => api.refresh());
  }, []);

  const handleChange = useCallback((_elements, appState) => {
    const tool = appState.activeTool;
    if (!NO_LOCK_TOOLS.has(tool.type) && !tool.locked) {
      apiRef.current?.setActiveTool({ type: tool.type, locked: true });
    }
  }, []);

  return (
    <div className="lk-wb-hero">
      {ready && (
        <>
          <Excalidraw
            excalidrawAPI={setAPI}
            onChange={handleChange}
            initialData={{ appState: { currentItemRoundness: 'sharp' } }}
            theme="dark"
            UIOptions={{
              canvasActions: {
                export: false,
                loadScene: false,
                saveToActiveFile: false,
                toggleTheme: false,
              },
            }}
          />
          <button
            className="lk-wb-export-btn"
            title="Export whiteboard as PDF"
            onClick={() => apiRef.current && downloadAsPDF(apiRef.current)}
          >
            PDF
          </button>
        </>
      )}
    </div>
  );
}
